/** Monatsrückblick als Story (MP-B): Karten zum Durchwischen (scroll-snap) – Umsatz, verkauft, bester Tag, Bestseller, Vergleich; als Bild teilbar. */
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, Icon, Notice, Seitenzustand } from "../ui";
import { formatEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { useMieterStart } from "./api";
import { fotoUrl } from "./artikel-api";
import { useRueckblick, type Rueckblick as RueckblickDaten } from "./konto-api";
import { useTx } from "../i18n";

function monatVerschieben(m: string, delta: number): string {
  const d = new Date(`${m}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + delta);
  return d.toISOString().slice(0, 7);
}

async function storyBild(r: RueckblickDaten, ladenName: string): Promise<Blob> {
  const c = document.createElement("canvas");
  c.width = 1080;
  c.height = 1350;
  const g = c.getContext("2d")!;
  const stil = getComputedStyle(document.documentElement);
  const t = (name: string) => stil.getPropertyValue(name).trim();
  const violett = t("--violett");
  const limette = t("--limette");
  const weiss = t("--on-violett");
  g.fillStyle = violett;
  g.fillRect(0, 0, c.width, c.height);
  g.fillStyle = weiss;
  g.font = "600 44px Inter, system-ui, sans-serif";
  g.fillText(`Mein ${r.name}`, 80, 160);
  g.font = "bold 180px Inter, system-ui, sans-serif";
  g.fillStyle = limette;
  g.fillText(formatEuro(r.umsatzCent).replace(" ", " "), 80, 380);
  g.fillStyle = weiss;
  g.font = "600 52px Inter, system-ui, sans-serif";
  g.fillText(`${r.anzahl} ${r.anzahl === 1 ? "Artikel" : "Artikel"} verkauft`, 80, 470);
  let y = 620;
  g.font = "44px Inter, system-ui, sans-serif";
  if (r.besterTag) { g.fillText(`Bester Tag: ${formatTag(r.besterTag.tag)} · ${formatEuro(r.besterTag.summeCent).replace(" ", " ")}`, 80, y); y += 80; }
  if (r.kategorien[0]) { g.fillText(`Läuft am besten: ${r.kategorien[0].kategorie}`, 80, y); y += 80; }
  if (r.vormonat.umsatzCent) { const diff = r.umsatzCent - r.vormonat.umsatzCent; g.fillText(`${diff >= 0 ? "+" : "−"}${formatEuro(Math.abs(diff)).replace(" ", " ")} zum Vormonat`, 80, y); y += 80; }
  g.fillStyle = limette;
  g.fillRect(80, 1180, 920, 6);
  g.fillStyle = weiss;
  g.font = "600 40px Inter, system-ui, sans-serif";
  g.fillText(`bei ${ladenName}`, 80, 1260);
  return new Promise((ok, nein) => c.toBlob((b) => (b ? ok(b) : nein(new Error("Bild konnte nicht erzeugt werden."))), "image/png"));
}

export function Rueckblick() {
  const tx = useTx();
  const [params, setParams] = useSearchParams();
  const vormonat = monatVerschieben(new Date().toISOString().slice(0, 7), -1);
  const monat = params.get("monat") ?? vormonat;
  const q = useRueckblick(monat);
  const start = useMieterStart();
  const [meldung, setMeldung] = useState<string | null>(null);

  async function teilenKlick(r: RueckblickDaten) {
    try {
      const blob = await storyBild(r, start.data?.laden.name ?? "");
      const datei = new File([blob], `rueckblick-${r.monat}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [datei] })) await navigator.share({ files: [datei], title: `Mein ${r.name}` });
      else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = datei.name;
        a.click();
        setMeldung("Bild gespeichert.");
      }
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : "Das hat nicht geklappt.");
    }
  }

  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <div className="sc-spread">
        <div className="sc-row">
          <Link to="/mieter/abrechnungen" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
          <h1 className="title">{tx("Rückblick")}</h1>
        </div>
        <span className="sc-row">
          <Button variant="quiet" icon="chevron-left" aria-label="Vormonat" onClick={() => setParams({ monat: monatVerschieben(monat, -1) })} />
          <Button variant="quiet" icon="chevron-right" aria-label="Nächster Monat" disabled={monat >= vormonat} onClick={() => setParams({ monat: monatVerschieben(monat, 1) })} />
        </span>
      </div>
      {meldung ? <Notice tone="info">{meldung}</Notice> : null}
      <Seitenzustand query={q} lines={6}>
        {(r) => {
          const diff = r.umsatzCent - r.vormonat.umsatzCent;
          const karten = [
            { k: "umsatz", el: <><span className="mp-story__label">{r.name}</span><span className="mp-story__gross">{formatEuro(r.umsatzCent)}</span><span className="mp-story__text">{r.anzahl ? `${r.anzahl} ${r.anzahl === 1 ? "Artikel" : "Artikel"} verkauft` : "Diesen Monat nichts verkauft – der nächste wird besser."}</span></> },
            ...(r.besterTag ? [{ k: "tag", el: <><span className="mp-story__label">Dein bester Tag</span><span className="mp-story__gross" style={{ fontSize: 40 }}>{formatTag(r.besterTag.tag)}</span><span className="mp-story__text">{r.besterTag.anzahl} verkauft · {formatEuro(r.besterTag.summeCent)}</span></> }] : []),
            ...(r.teuerster ? [{ k: "top", el: <><span className="mp-story__label">Teuerster Verkauf</span>{r.teuerster.fotoKey ? <img src={fotoUrl(r.teuerster.fotoKey)} alt="" className="mp-story__bild" /> : null}<span className="mp-story__gross" style={{ fontSize: 36 }}>{r.teuerster.bezeichnung}</span><span className="mp-story__text">{formatEuro(r.teuerster.preisCent)}</span></> }] : []),
            ...(r.schnellster ? [{ k: "schnell", el: <><span className="mp-story__label">Am schnellsten weg</span>{r.schnellster.fotoKey ? <img src={fotoUrl(r.schnellster.fotoKey)} alt="" className="mp-story__bild" /> : null}<span className="mp-story__gross" style={{ fontSize: 36 }}>{r.schnellster.bezeichnung}</span><span className="mp-story__text">nach {r.schnellster.tage} {r.schnellster.tage === 1 ? "Tag" : "Tagen"}</span></> }] : []),
            ...(r.kategorien.length ? [{ k: "kat", el: <><span className="mp-story__label">Läuft am besten</span>{r.kategorien.map((k) => <span key={k.kategorie} className="mp-story__gross" style={{ fontSize: 32 }}>{k.kategorie} <span className="sc-muted" style={{ fontSize: 20 }}>· {k.anzahl}</span></span>)}</> }] : []),
            { k: "vgl", el: <><span className="mp-story__label">Im Vergleich zum Vormonat</span><span className="mp-story__gross" style={{ fontSize: 48 }}>{r.vormonat.umsatzCent || r.umsatzCent ? `${diff >= 0 ? "+" : "−"}${formatEuro(Math.abs(diff))}` : "–"}</span><span className="mp-story__text">{formatEuro(r.vormonat.umsatzCent)} im Vormonat</span></> },
          ];
          return (
            <>
              <div className="mp-story" role="region" tabIndex={0} aria-label="Rückblick-Karten, seitlich wischen">
                {karten.map((k, i) => (
                  <section key={k.k} className={`mp-story__karte${i % 2 ? " is-alt" : ""}`}>
                    {k.el}
                    <span className="mp-story__nr">{i + 1} / {karten.length}</span>
                  </section>
                ))}
              </div>
              <div className="sc-toolbar">
                <Button variant="primary" icon="sparkles" onClick={() => void teilenKlick(r)}>Als Bild teilen</Button>
                <Link to="/mieter/konto" className="rg-btn rg-btn--quiet">Zum Kontoauszug</Link>
              </div>
              <p className="sc-muted sc-small"><Icon name="chevron-right" size={14} /> Wischen für die nächste Karte.</p>
            </>
          );
        }}
      </Seitenzustand>
    </div>
  );
}
